/**
 * the schema plugin module
 */

import _ from 'underscore';
import { get } from '@bingads-webui-universal/primitive-utilities';
import {
  hasOwnProperty,
  defineConstProperty,
} from '@bingads-webui/reflection';
import { Registry } from '@bingads-webui/registry';

import typesPlugin from './types-plugin';
import {
  ISchema,
  ITypeInfo,
  ICallableDefinition,
  ICallableCollection,
  IEDMOptions,
  IEdm,
  IEdmTypes,
} from './types';

function getSchemaName(schema: ISchema) {
  const { $$ODataExtension, name } = schema;
  if ($$ODataExtension) {
    return $$ODataExtension.Name;
  }

  // deprecated
  // There is no such keyword as "name" in JSON Schema, it's specific to OData
  // See:
  //  http://json-schema.org/latest/json-schema-core.html
  //  http://json-schema.org/latest/json-schema-validation.html
  return name;
}

function getSchemaBaseTypeName(schema: ISchema) {
  const { $$ODataExtension, clrTypeBase } = schema;
  if ($$ODataExtension) {
    return $$ODataExtension.BaseType && $$ODataExtension.BaseType.$ref;
  }

  // deprecated
  // There is no such keyword as "clrTypeBase" in JSON Schema, it's specific to OData
  // See:
  //  http://json-schema.org/latest/json-schema-core.html
  //  http://json-schema.org/latest/json-schema-validation.html
  return clrTypeBase;
}

function getSchemaKey(schema: ISchema) {
  const { $$ODataExtension, key } = schema;
  if ($$ODataExtension) {
    return $$ODataExtension.Key;
  }

  // deprecated
  // There is no such keyword as "key" in JSON Schema, it's specific to OData
  // See:
  //  http://json-schema.org/latest/json-schema-core.html
  //  http://json-schema.org/latest/json-schema-validation.html
  return key;
}

function getSchemaNavigationPropertyNames(schema: ISchema) {
  const { $$ODataExtension } = schema;
  if ($$ODataExtension) {
    return $$ODataExtension.NavigationProperty;
  }

  return [];
}

export default (edm: IEdm, {
  schemas = {},
  // TODO: deprecated, every schemas should be able to have different namespaces
  // we are still using it with legacy schemas
  namespace = null,
} : IEDMOptions = {}) => {
  if (hasOwnProperty(edm, 'schema')) {
    return;
  }

  typesPlugin(edm);

  const {
    CollectionType,
    EntityType,
    PrimitiveType,
    ComplexType,
    OneOfType,
    ActionType,
    FunctionType,
  } = edm.types as IEdmTypes;

  defineConstProperty(edm, 'schema', (() => {
    function getSchemaFullName(schema: ISchema, path?: string) {
      // $$ref exists in resolved schema
      const possiblePath = (_.isString(path) && path) || schema.$$ref || schema.$ref;
      return possiblePath
        ? possiblePath.replace(/\//g, '.')
        : Registry.getQualifiedName(getSchemaName(schema)!, namespace!);
    }

    /**
     * Get type name object with its information
     * @param {Schema[]} dependencies - An output paramter, a queue of dependent schemas
     * @param {Object} typeInfo - The schema information of the property
     * @return {String} The type name
     */
    function getTypeName(dependencies: ISchema[], typeInfo: ITypeInfo): string | null {
      let schema: ISchema | undefined | null = null;
      let typeName: string | null = null;

      if (!typeInfo) {
        return null;
      }

      if (typeInfo.type === 'array') {
        return CollectionType.collectionTypeName(getTypeName(dependencies, typeInfo.items as ITypeInfo));
      }
      if (typeInfo.oneOf) {
        return OneOfType.oneOfTypeName(typeInfo.oneOf.map((item: ITypeInfo) => getTypeName(dependencies, item)));
      }
      if (typeInfo.type === 'object') {
        ({ schema } = typeInfo);
        // it's possible for the schema to be undefined, use 'object' type by default
        typeName = getSchemaFullName(schema || typeInfo);
      } else if (typeInfo.type === 'string' && typeInfo.enum) {
        // enum type is string, but we need to get a meanful name for different enum type
        // this is to help us disable cache accurately, as enum can be odata call return type now
        typeName = (typeInfo.$$ref || 'string').split('/').join('.');
      } else {
        // primitive types
        typeName = typeInfo.type!;
      }

      if (schema) {
        dependencies.push(schema);
      }

      return typeName;
    }

    function defineCallableOnType(
      acts: Record<string, ICallableDefinition>,
      funcs: Record<string, ICallableDefinition>,
      type: any,
      qualifiedName: string,
      forEntityType: boolean = true
    ) {
      const actions: any[] = [];
      const functions: any[] = [];

      function getCallableTypeName(key: string) {
        return `${key}##${qualifiedName}${forEntityType ? '' : '@@COLL'}`;
      }

      _.each(_.keys(acts), (key: string) => {
        const typeName = getCallableTypeName(key);
        const parameters = _.mapObject(
          acts[key].Parameter,
          (parameter: ITypeInfo) => getTypeName([], parameter)
        );

        actions.push(new ActionType({
          name: typeName,
          callableName: key,
          parameters,
          returnTypeName: getTypeName([], acts[key].ReturnType),
        }));
      });

      _.each(_.keys(funcs), (key: string) => {
        const typeName = getCallableTypeName(key);
        const parameters = _.mapObject(
          funcs[key].Parameter,
          (parameter: ITypeInfo) => getTypeName([], parameter)
        );

        functions.push(new FunctionType({
          name: typeName,
          callableName: key,
          parameters,
          returnTypeName: getTypeName([], funcs[key].ReturnType),
        }));
      });

      defineConstProperty(type, 'callable', {
        actions,
        functions,
      });
    }

    /**
     * Define an entity type, and its dependencies
     *
     * @param {Schema} schema - The schema for the entity type
     * @param {string} [path] - Possible full path of the schema
     * @return {Void} Nothing to return
     */
    function defineSchemaType(schema: ISchema, path?: string): void {
      const qualifiedName = getSchemaFullName(schema, path);

      if (!edm.types!.resolve(qualifiedName)) {
        const dependencies: ISchema[] = [];
        const properties = _.mapObject(schema.properties, (typeInfo: ITypeInfo) => ({
          typeName: getTypeName(dependencies, typeInfo),
        }));

        const type = new EntityType({
          name: qualifiedName,
          baseTypeName: getSchemaBaseTypeName(schema) || 'object',
          key: getSchemaKey(schema),
          properties,
          navigationPropertyNames: getSchemaNavigationPropertyNames(schema) as any,
        });

        defineConstProperty(type, 'schema', schema);

        _.each(dependencies as any, defineSchemaType);

        const entityActions = get(schema, '$$ODataExtension.Action', null);
        const entityFunctions = get(schema, '$$ODataExtension.Function', null);

        if (entityActions || entityFunctions) {
          defineCallableOnType(entityActions!, entityFunctions!, type, qualifiedName);
        }

        const collCallable = get(schema, '$$ODataExtension.Collection', null);

        if (collCallable) {
          defineCallableOnType(
            (collCallable as ICallableCollection).Action,
            (collCallable as ICallableCollection).Function,
            type.collectionType,
            qualifiedName,
            false
          );
        }
      }
    }

    // Define all the primitive types
    _.each({
      string: String,
      integer: Number,
      number: Number,
      datetime: Date,
      boolean: Boolean,
      null: null,
    }, (
      jsType: StringConstructor | NumberConstructor | DateConstructor | BooleanConstructor | null,
      name: string
    ) => new PrimitiveType({ name, jsType }));

    // Define the base object type
    /* eslint no-new: 0 */
    new ComplexType({
      name: 'object',
      properties: {},
    });

    _.each(schemas, (schema: ISchema, index: string | number) => {
      if (_.isString(index)) {
        defineSchemaType(schema, index);
      } else {
        defineSchemaType(schema);
      }
    });

    return (_.chain({}) as any)
      .defineConstProperty('schemas', schemas)
      .defineConstProperty('namespace', namespace)
      .value();
  })());
};
